import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../theme/colors';
import { spacing } from '../theme/spacing';
import { typography } from '../theme/typography';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useTypography } from '../hooks/useTypography';
import { SystemAlertItem } from '../hooks/useSystemAlerts';
import { useThemedStyles } from '../hooks/useThemedStyles';

const DEFAULT_PREVIEW_LIMIT = 5;

const TYPE_COLOR: Record<string, string> = {
  error: '#EF4444',
  warning: '#F59E0B',
  system: '#6B7280',
  success: '#10B981',
};

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

export interface SystemAlertsTableProps {
  alerts: SystemAlertItem[];
  previewLimit?: number;
}

/** System alerts preview list — read/unread visual language matches
 * NotificationItem.tsx's existing convention (unread dot + bold title) for
 * consistency with the rest of the app's notification UI. */
export const SystemAlertsTable: React.FC<SystemAlertsTableProps> = ({ alerts, previewLimit = DEFAULT_PREVIEW_LIMIT }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const typo = useTypography();
  const preview = alerts.slice(0, previewLimit);

  return (
    <View style={styles.list}>
      {preview.map((a) => (
        <View key={a._id} style={[styles.row, { flexDirection: rtl.rowDirection }]}>
          <View style={[styles.dot, { backgroundColor: TYPE_COLOR[a.type ?? 'system'] ?? TYPE_COLOR.system }]} />
          <View style={styles.rowContent}>
            <Text
              style={[styles.title, !a.read && styles.unreadTitle, { textAlign: rtl.textStart, fontFamily: a.read ? typo.fontFamily.primary : typo.fontFamily.semibold }]}
              numberOfLines={1}
            >
              {a.title}
            </Text>
            {!!a.body && (
              <Text style={[styles.body, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]} numberOfLines={1}>
                {a.body}
              </Text>
            )}
          </View>
          <View style={styles.rowEnd}>
            {!a.read && <View style={styles.unreadDot} />}
            <Text style={[styles.time, { fontFamily: typo.fontFamily.primary }]}>{timeAgo(a.createdAt)}</Text>
          </View>
        </View>
      ))}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  list: {
    gap: spacing.sm,
  },
  row: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  unreadTitle: {
    color: colors.textDark,
  },
  body: {
    fontSize: 11,
    color: colors.textLight,
  },
  rowEnd: {
    alignItems: 'flex-end',
    gap: 2,
  },
  unreadDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.primary,
  },
  time: {
    fontSize: 10,
    color: colors.textLight,
  },
});
